import { Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin, map, Observable, of, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { AutoCompleteSelectEvent } from 'primeng/autocomplete';
import _ from 'lodash';
//* Services
import { BreadcrumbService, ModalService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { ApiResponse, AuthService } from '@card-software/nexus-utilities';
//* Models
import { Asset } from '@app/models/asset/asset';
import { Greige } from '@app/models/greige/greige';
import { Roll, WeightType, WeightTypeLabelKeys } from '@app/models/knitting/roll';
import { AwsIotWeightMessage, SimpleWeightMessage, WeightStationMessage } from '@app/models/mqtt/weight-station-message';
import { Fabric } from '@app/models/order/work-order';
import { KnittingOrder } from '@app/models/production/knitting-order';
import { ProductionCard, ProductionCardSearchCommand } from '@app/models/production/production-card';
import { ProductionCardBundle, ProductionCardBundleStatus } from '@app/models/production/production-card/production-card-bundle';
import { StationsRoles } from '@app/models/roles/roles-group';
import { UserFull } from '@app/models/user-master/user-lite';
import { AssetProxyService } from '@app/proxies/asset/asset.proxy.service';
import { FabricProxyService } from '@app/proxies/item/fabric.proxy.service';
import { FinishingRouteProxyService } from '@app/proxies/production/finishing-route.proxy.service';
import { KnittingOrderProxyService } from '@app/proxies/production/knitting-order.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { ProductionCardBundleProxyService } from '@app/proxies/production/production-card-bundle.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { RollLabelProxyService } from '@app/proxies/production/roll-label.proxy.service';
import { UsersProxyService } from '@app/proxies/users.proxy.service';
import { AwsIotMqttService } from '@app/services/aws-iot-mqtt.service';
import { BarcodeScannerService } from '@app/services/barcode-scanner.service';
import { KnittingMachineRolls, KnittingMachineRollsService } from '@app/services/knitting-machine-rolls.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { MqttService } from '@app/services/mqtt.service';
import { FinishingRouteUtil } from '@app/utilities/finishing-route.util';
import { WeightStationAccessUtil } from '@app/utilities/weight-station-access.util';
//* Components
import { RollDetailModalComponent } from '@app/components/shared/_modals/roll-detail-modal/roll-detail-modal.component';

// Asset type constants for weight station modes
// The former "Final Weight Station" type was split in two: Finishing keeps the original id
const ASSET_TYPE_FINISHING_WEIGHT = '695d102d1d4ad8ec4c89e352';
const ASSET_TYPE_ROLLING_WEIGHT = '6a669f801d4ad8ec4c89e401';
const ASSET_TYPE_GREIGE_WEIGHT = '67abfb9aee06eac3505f9834';
const ASSET_TYPE_BUNDLE_WEIGHT = '695d117f1d4ad8ec4c89e357';

// MQTT topic mapping for weight stations
// Stations not in this map will not subscribe to MQTT
// defaultUnit: the unit to display before receiving data from MQTT
const WEIGHT_STATION_TOPIC_MAP: Record<string, { topic: string; isSimple: boolean; defaultUnit: 'lb' | 'kg' }> = {
  'WST-1': { topic: 'knitting_section/scale_1/weight', isSimple: false, defaultUnit: 'lb' },
  'WST-20': { topic: 'nt/batching/wst-20/weight', isSimple: true, defaultUnit: 'kg' },
  'WST-30': { topic: 'nt/final-weight-stations/wst-30/weight', isSimple: true, defaultUnit: 'lb' },
  'WST-31': { topic: 'nt/final-weight-stations/wst-31/weight', isSimple: true, defaultUnit: 'lb' },
  'WST-2': { topic: 'knitting_section/scale_2/weight', isSimple: true, defaultUnit: 'lb' },
};

// Tare options for bundle mode (weight in kg)
export interface TareOption {
  labelEn: string;
  labelEs: string;
  weightKg: number;
}

const BUNDLE_TARE_OPTIONS: TareOption[] = [
  { labelEn: 'Small Pallet', labelEs: 'Rampla Pequeña', weightKg: 9 },
  { labelEn: 'Big Pallet', labelEs: 'Rampla Grande', weightKg: 10 },
  { labelEn: 'Small Truck', labelEs: 'Troco Pequeño', weightKg: 53 },
  { labelEn: 'Big Truck', labelEs: 'Troco Grande', weightKg: 100 },
];

@Component({
  selector: 'app-weight-station-detail',
  templateUrl: './weight-station-detail.component.html',
  styleUrls: ['./weight-station-detail.component.scss'],
})
export class WeightStationDetailComponent implements OnInit, OnDestroy {
  @ViewChild('rollCodeInput', { static: false }) rollCodeInput?: ElementRef<HTMLInputElement>;
  @ViewChild('focusButton', { static: false }) focusButton?: ElementRef<HTMLButtonElement>;

  private expectingPrint = false;

  private focusForBarcodeScanner() {
    // Focus the hidden button so barcode scanner works but no input interference
    setTimeout(() => {
      const el = this.focusButton?.nativeElement;
      if (el) {
        el.focus();
      }
    }, 100);
  }

  showWeightInKgs = signal(false);

  // Station asset loaded from the weight station ID
  stationAsset = signal<Asset | null>(null);

  // Bundle mode - determined by asset type
  get isBundleMode(): boolean {
    const assetType = this.stationAsset()?.assetTypeReference;
    return assetType === ASSET_TYPE_BUNDLE_WEIGHT;
  }

  // Finishing weight mode - final weight taken for routes without the Rolling process
  get isFinishingWeightMode(): boolean {
    const assetType = this.stationAsset()?.assetTypeReference;
    return assetType === ASSET_TYPE_FINISHING_WEIGHT;
  }

  // Rolling weight mode - final weight taken for routes that include the Rolling process
  get isRollingWeightMode(): boolean {
    const assetType = this.stationAsset()?.assetTypeReference;
    return assetType === ASSET_TYPE_ROLLING_WEIGHT;
  }

  // Final weight mode - both Finishing and Rolling stations record the roll's final weight
  get isFinalWeightMode(): boolean {
    return this.isFinishingWeightMode || this.isRollingWeightMode;
  }

  // Area configured on the asset in Asset.UI; the header shows it when set
  stationArea = computed(() => this.stationAsset()?.weightStationDetails?.area?.trim() || null);

  // Greige/first weight mode - determined by asset type
  get isGreigeWeightMode(): boolean {
    const assetType = this.stationAsset()?.assetTypeReference;
    return assetType === ASSET_TYPE_GREIGE_WEIGHT;
  }

  get searchLabel(): string {
    if (this.isBundleMode) {
      return 'Scan Bundle Code';
    }
    return this.isFinalWeightMode ? 'Scan Roll Code (Final Weight)' : 'Scan Roll Code';
  }

  currentBundle = signal<ProductionCardBundle | null>(null);
  currentRoll = signal<Roll | null>(null);
  currentGreige = signal<Greige | null>(null);
  currentKnittingOrder = signal<KnittingOrder | null>(null);
  currentAsset = signal<Asset | null>(null);
  bundleFabrics = signal<Fabric[]>([]);
  searchInput = new FormControl('');
  currentReferenceStation = signal<string>('');
  blockSaveButton = signal(false);
  manualWeightValue = 0;
  manualWeightOverride = false;
  // Tare selection for bundle mode
  tareOptions = BUNDLE_TARE_OPTIONS;
  selectedTare = new FormControl<TareOption | null>(null);
  // Autocomplete properties
  operatorControl = new FormControl<UserFull | null>(null);
  productionCardControl = new FormControl<ProductionCard | null>(null);
  selectedProductionCard = signal<ProductionCard | null>(null);
  allUsers: UserFull[] = [];
  scaleData: WeightStationMessage | null = null;
  // Track latest weight values by status (for WST-1 style scales)
  weightValues = {
    UNKNOWN: null as WeightStationMessage | null,
    NET: null as WeightStationMessage | null,
    GROSS: null as WeightStationMessage | null,
    TARE: null as WeightStationMessage | null,
  };
  // Simple weight value for stations like WST-20, WST-30 (no status, just value + unit)
  simpleWeightData: SimpleWeightMessage | null = null;
  // Whether we're using simple weight mode (no Tare/NET/GROSS)
  isSimpleWeightMode = false;
  // Store subscriptions from rxjs observables (if any)
  subscriptions: Subscription[] = [];

  isLoading = 0;

  plannedLbs = 0;
  remainingLbs = 0;
  expectedFabricCode = '';

  // roles section
  blockSection = true;
  blockAutoCompleteInputs = true;

  private readonly modalService = inject(ModalService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly rollProxyService = inject(RollProxyService);
  private readonly assetProxyService = inject(AssetProxyService);
  private readonly fabricProxyService = inject(FabricProxyService);
  private readonly knittingOrderProxyService = inject(KnittingOrderProxyService);
  private readonly mqttService = inject(MqttService);
  private readonly awsIotMqttService = inject(AwsIotMqttService);
  private confirmationService = inject(ConfirmationService);
  private readonly barcodeScannerService = inject(BarcodeScannerService);
  private readonly rollPrintProxyService = inject(RollLabelProxyService);
  private readonly usersProxyService = inject(UsersProxyService);
  private translateService = inject(TranslateService);
  private knittingMachineRollsService = inject(KnittingMachineRollsService);
  private readonly localeHelperService = inject(LocaleHelperService);
  private readonly productionCardBundleProxyService = inject(ProductionCardBundleProxyService);
  private readonly productionCardsProxyService = inject(ProductionCardsProxyService);
  private readonly finishingRouteProxyService = inject(FinishingRouteProxyService);

  openRollDetailModal() {
    this.modalService.open(
      RollDetailModalComponent,
      { modalClass: 'modal_small' },
      {
        roll: this.currentRoll(),
        greige: this.currentGreige(),
      },
    );
  }

  private breadcrumbService = inject(BreadcrumbService);

  constructor() {
    effect(
      () => {
        const assetId = this.currentReferenceStation() ?? '';
        this.breadcrumbService.set([
          { label: 'Breadcrumb_Knitting' },
          { label: 'Stations', route: '/stations' },
          {
            label: `${this.translateService.instant('WeightStation')}: ${assetId}`,
            route: ['/stations', 'weight-station'],
            queryParams: { id: assetId },
          },
          { label: 'Breadcrumb_Weight_Station_Detail' },
        ]);
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.canEdit();
    const idWST = this.activatedRoute.snapshot.queryParamMap.get('id');
    this.currentReferenceStation.set(idWST!);
    // The scale subscription depends on the asset (it carries the MQTT topic),
    // so it is started from loadStationAsset once the asset is available.
    this.loadStationAsset(idWST!);
    this.loadUsers();

    // Keep selectedProductionCard signal in sync with the form control. PrimeNG's
    // p-autocomplete with forceSelection can reset the value without firing (onSelect),
    // which previously left the signal stale and silently aborted Final Weight searches.
    this.subscriptions.push(
      this.productionCardControl.valueChanges.subscribe((value) => {
        this.selectedProductionCard.set((value as ProductionCard) ?? null);
      }),
    );

    // Focus hidden button for barcode scanner readiness
    setTimeout(() => this.focusForBarcodeScanner(), 200);

    this.barcodeScannerService.barcode$.subscribe((barcode) => {
      const activeTag = document.activeElement?.tagName.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') {
        return;
      }

      this.searchInput.setValue(barcode);
      this.searchRollByCode();
    });
  }

  get events() {
    if (this.currentRoll()) {
      // Final Weight is already part of stationTimeStamps (PBI #4390), so it no longer
      // needs to be appended here as a separate synthetic row.
      return (
        this.currentRoll()?.stationTimeStamps?.map((stationTimeStamp) => {
          return {
            asset: `${stationTimeStamp.stationAssetId}`,
            date: new Date(stationTimeStamp.timeStamp).toLocaleString(),
            weightValue: stationTimeStamp.weightInLbs ?? 0,
            weightType: stationTimeStamp.weightType,
          };
        }) || []
      );
    }
    return [];
  }

  weightTypeLabel(type: WeightType | undefined | null): string {
    if (type === undefined || type === null) {
      return '';
    }
    const key = WeightTypeLabelKeys[type];
    return key ? this.localeHelperService.translateWithFallback(key, WeightType[type]) : '';
  }

  getRollStationWeight() {
    if (this.currentRoll() && this.blockSaveButton()) {
      const filterStations = this.currentRoll()?.stationTimeStamps?.filter((stationTimeStamp) => {
        return stationTimeStamp.stationAssetId === this.currentReferenceStation();
      });

      if (filterStations && filterStations.length > 0) {
        return filterStations[0].weightInLbs;
      }
    }
    return 0;
  }

  /**
   * Stations whose asset carries a `weightTopic` read the scale from the AWS IoT
   * broker on that topic; the rest keep using the legacy HiveMQ broker with the
   * hardcoded topic map.
   */
  private subscribeToScale(): void {
    const assetTopic = this.stationAsset()?.weightStationDetails?.weightTopic?.trim();

    if (assetTopic) {
      this.subscribeToAssetTopic(assetTopic);
      return;
    }

    this.subscribeToLegacyScale();
  }

  /** AWS IoT: one topic per asset, payload is `{ assetId, weight, unit, timestamp }`. */
  private subscribeToAssetTopic(topic: string): void {
    // These scales report a single value + unit, with no Tare/NET/GROSS status.
    this.isSimpleWeightMode = true;

    // Keep the station's known unit while waiting for the first message, so the
    // display doesn't flip units a moment after loading.
    const legacyConfig = WEIGHT_STATION_TOPIC_MAP[this.currentReferenceStation()];
    if (legacyConfig) {
      this.showWeightInKgs.set(legacyConfig.defaultUnit === 'kg');
    }

    if (!this.awsIotMqttService.isConfigured) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Stations_Weight_Station_Detail_Warning'),
        this.localeHelperService.translateWithFallback(
          'Stations_Weight_Station_Detail_Scale_Connection_Error',
          'Could not connect to the scale, enter the weight manually',
        ),
      );
      return;
    }

    this.subscriptions.push(
      this.awsIotMqttService.subscribe(topic).subscribe({
        next: (message: AwsIotWeightMessage) => this.applySimpleWeight(message.weight, message.unit),
        error: (error: unknown) => {
          console.error(`Error on AWS IoT topic "${topic}":`, error);
          GlobalMessageUtil.warn(
            this.translateService.instant('Stations_Weight_Station_Detail_Warning'),
            this.localeHelperService.translateWithFallback(
              'Stations_Weight_Station_Detail_Scale_Connection_Error',
              'Could not connect to the scale, enter the weight manually',
            ),
          );
        },
      }),
    );
  }

  /**
   * Stores a single scale reading. Values are always kept in lbs internally (the
   * API only accepts lbs); `showValuesInKgs` handles the display conversion.
   */
  private applySimpleWeight(weight: number, unit: string): void {
    const isKg = unit.toLowerCase() === 'kg';
    const weightInLbs = isKg ? weight * 2.20462 : weight;

    this.simpleWeightData = { weight: weightInLbs, unit: isKg ? 'kg' : 'lb' };
    this.showWeightInKgs.set(isKg);
  }

  private subscribeToLegacyScale(): void {
    const stationId = this.currentReferenceStation();
    const topicConfig = WEIGHT_STATION_TOPIC_MAP[stationId];

    // If no mapping exists for this station, don't subscribe
    if (!topicConfig) {
      return;
    }

    this.isSimpleWeightMode = topicConfig.isSimple;

    // Set default unit before receiving data
    this.showWeightInKgs.set(topicConfig.defaultUnit === 'kg');

    this.subscriptions.push(
      this.mqttService.subscribe(topicConfig.topic).subscribe((message) => {
        if (topicConfig.isSimple) {
          // Simple weight message (WST-20, WST-30) - just value + unit
          const simpleMessage = message as SimpleWeightMessage;

          this.applySimpleWeight(simpleMessage.weight, simpleMessage.unit);

          console.log(
            'Simple scale data (normalized to lbs):',
            this.simpleWeightData,
            'Original:',
            simpleMessage.weight,
            simpleMessage.unit,
          );
        } else {
          // Full weight message (WST-1) with status
          this.scaleData = message;

          // Store the latest weight value for each status
          if (message.status === 'UNKNOWN' || message.status === 'NET' || message.status === 'GROSS' || message.status === 'TARE') {
            this.weightValues[message.status] = message;
          }

          console.log('Scale data:', this.scaleData);
          console.log('Weight values:', this.weightValues);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  search() {
    const searchTerm = this.searchInput.value;
    if (_.isEmpty(searchTerm)) {
      GlobalMessageUtil.warn(
        this.translateService.instant('Stations_Weight_Station_Detail_Warning'),
        this.translateService.instant('Stations_Weight_Station_Detail_Enter_Code_To_Search'),
      );
      return;
    }
    this.blockSaveButton.set(false);

    if (this.isBundleMode) {
      this.searchBundleByNumber();
    } else {
      this.searchRollByCode();
    }
  }

  searchBundleByNumber() {
    // Reset state before searching
    this.blockSaveButton.set(false);
    this.currentBundle.set(null);
    this.bundleFabrics.set([]);
    this.resetManualWeight();

    this.isLoading++;
    this.productionCardBundleProxyService.getByBundleNumber(this.searchInput.value!).subscribe({
      next: (response) => {
        this.isLoading--;
        if (response.data) {
          this.currentBundle.set(response.data);
          const statuses = [ProductionCardBundleStatus.Weighed, ProductionCardBundleStatus.BRD];
          if (statuses.includes(response.data.status)) {
            this.blockSaveButton.set(true);
          }

          // Load fabric data for bundle rolls to enable yards calculation
          const fabricCodes = [...new Set(response.data.rolls?.map((roll) => roll.expectedFabricCode).filter(Boolean) as string[])];
          if (fabricCodes.length > 0) {
            this.isLoading++;
            this.fabricProxyService.batchFabrics(fabricCodes).subscribe({
              next: (fabricResponse) => {
                this.bundleFabrics.set(fabricResponse.data || []);
                this.isLoading--;
              },
              error: () => {
                this.isLoading--;
              },
            });
          }
        } else {
          GlobalMessageUtil.error(
            this.translateService.instant('Stations_Weight_Station_Detail_Error'),
            this.translateService.instant('Stations_Weight_Station_Detail_Bundle_Not_Found'),
          );
        }
      },
      error: () => {
        this.isLoading--;
        this.currentBundle.set(null);
        this.bundleFabrics.set([]);
      },
    });
  }

  searchRollByCode() {
    this.blockSaveButton.set(false);
    this.currentRoll.set(null);
    this.resetManualWeight();
    const code = this.searchInput.value;
    if (_.isEmpty(code)) {
      GlobalMessageUtil.error(
        this.translateService.instant('Stations_Weight_Station_Detail_Error'),
        this.translateService.instant('Stations_Weight_Station_Detail_Enter_Roll_Code'),
      );
      return;
    }
    if (this.isFinalWeightMode && !this.selectedProductionCard()) {
      GlobalMessageUtil.error(
        this.translateService.instant('Stations_Weight_Station_Detail_Error'),
        this.translateService.instant('Stations_Weight_Station_Detail_Production_Card_Required'),
      );
      return;
    }
    this.isLoading++;

    this.rollProxyService.getRollsByNumbers([code!]).subscribe({
      next: (response) => {
        this.isLoading--;
        if (!_.isEmpty(response.data)) {
          const roll = response.data?.find((roll) => {
            return roll.number?.toLowerCase() === code?.toLowerCase();
          });
          if (roll) {
            this.currentRoll.set(roll);

            if (this.isFinalWeightMode && !this.isRollLinkedToSelectedProductionCard(roll)) {
              GlobalMessageUtil.error(
                this.translateService.instant('Stations_Weight_Station_Detail_Error'),
                this.translateService.instant('Stations_Weight_Station_Detail_Roll_Not_In_Production_Card', {
                  rollNumber: roll.number ?? '',
                }),
              );
              this.resetRollSearchState();
              return;
            }

            const operatorUser = this.allUsers.find((user) => user.username === roll.createdBy);
            if (operatorUser) {
              this.operatorControl.setValue(operatorUser);
            }
            if (this.isFinalWeightMode) {
              this.validateFinishingRouteForStationType(roll);
            } else {
              this.verifyRollPast();
            }
            this.complementSearch(
              this.currentRoll()?.assetId ?? '',
              this.currentRoll()?.expectedFabricCode ?? '',
              this.currentRoll()?.knittingOrderReference ?? '',
            );
          } else {
            GlobalMessageUtil.error(
              this.translateService.instant('Stations_Weight_Station_Detail_Error'),
              this.translateService.instant('Stations_Weight_Station_Detail_Roll_Not_Found'),
            );
            this.resetRollSearchState();
          }
        } else {
          GlobalMessageUtil.error(
            this.translateService.instant('Stations_Weight_Station_Detail_Error'),
            this.translateService.instant('Stations_Weight_Station_Detail_Roll_Not_Found'),
          );
        }
      },
      error: (err) => {
        this.isLoading--;
        throw err;
      },
    });
  }

  private isRollLinkedToSelectedProductionCard(roll: Roll): boolean {
    const linkedReference = roll.currentlyLinkedToProductionCardKey?.reference;
    return linkedReference === this.selectedProductionCard()?.id;
  }

  /**
   * A Rolling station only accepts rolls whose finishing route includes the Rolling process and
   * has already started it; a Finishing station only accepts routes without the Rolling process.
   * The same rules are enforced by the API when the final weight is saved.
   */
  private validateFinishingRouteForStationType(roll: Roll): void {
    this.blockSaveButton.set(true);
    this.isLoading++;

    this.finishingRouteProxyService
      .getByRollNumber(roll.number!)
      .pipe(finalize(() => this.isLoading--))
      .subscribe({
        next: (response) => {
          const route = response.data;
          if (!route) {
            this.rejectRollForStationType(
              'Stations_Weight_Station_Detail_Finishing_Route_Not_Found',
              'No finishing route was found for this roll',
            );
            return;
          }

          const hasRollingProcess = FinishingRouteUtil.hasRollingProcess(route);

          if (this.isRollingWeightMode) {
            if (!hasRollingProcess) {
              this.rejectRollForStationType(
                'Stations_Weight_Station_Detail_Route_Without_Rolling_Process',
                'The finishing route of this roll does not include the Rolling process, it cannot be weighed at a Rolling weight station',
              );
              return;
            }

            if (!FinishingRouteUtil.isRollingProcessStartedOrCompleted(route)) {
              this.rejectRollForStationType(
                'Stations_Weight_Station_Detail_Rolling_Process_Not_Started',
                'The Rolling process of this roll has not started yet, it cannot be weighed at a Rolling weight station',
              );
              return;
            }
          } else if (hasRollingProcess) {
            this.rejectRollForStationType(
              'Stations_Weight_Station_Detail_Route_With_Rolling_Process',
              'The finishing route of this roll includes the Rolling process, it must be weighed at a Rolling weight station',
            );
            return;
          }

          this.verifyRollPast();
        },
        error: () => {
          this.rejectRollForStationType(
            'Stations_Weight_Station_Detail_Finishing_Route_Not_Found',
            'No finishing route was found for this roll',
          );
        },
      });
  }

  private rejectRollForStationType(messageKey: string, fallback: string): void {
    GlobalMessageUtil.error(
      this.translateService.instant('Stations_Weight_Station_Detail_Error'),
      this.localeHelperService.translateWithFallback(messageKey, fallback),
    );
    this.currentRoll.set(null);
    this.currentGreige.set(null);
    this.currentAsset.set(null);
    this.currentKnittingOrder.set(null);
  }

  private resetRollSearchState(): void {
    this.currentRoll.set(null);
    this.currentGreige.set(null);
    this.currentAsset.set(null);
    this.currentKnittingOrder.set(null);
    this.isLoading--;
  }

  private resetManualWeight() {
    // Only clear manual weight value, keep scale/MQTT data as it's continuously updated
    // The actual weight from the scale should persist when searching for new rolls/bundles
    this.manualWeightValue = 0;
    this.manualWeightOverride = false;
  }

  private complementSearch(assetId: string, fabricCode: string, knittingOrderId: string) {
    this.isLoading++;

    const knittingOrderObs = knittingOrderId
      ? this.knittingOrderProxyService.getKnittingOrderByIdAndFabricCode(knittingOrderId, fabricCode)
      : of({ success: true, data: null, errorMessage: null } as ApiResponse<KnittingOrder | null>);

    const rollsByKnittingOrderObs = knittingOrderId
      ? this.rollProxyService.getRollsByKnittingOrder(knittingOrderId, true, true)
      : of({ success: true, data: [], errorMessage: null } as ApiResponse<Roll[]>);

    forkJoin([
      this.assetProxyService.filterAssets({ assetId }),
      this.fabricProxyService.batchFabrics([fabricCode]),
      knittingOrderObs,
      rollsByKnittingOrderObs,
    ]).subscribe({
      next: ([assetResult, fabricResult, knittingOrder, rolls]) => {
        this.currentAsset.set(assetResult.data?.[0] ?? null);
        if (fabricResult.data && fabricResult.data.length > 0) {
          this.currentGreige.set(fabricResult.data[0].greige);
        }
        this.plannedLbs = 0;
        this.currentKnittingOrder.set(knittingOrder.data);
        this.expectedFabricCode = this.currentRoll()
          ? this.isGreigeWeightMode
            ? (this.currentRoll()!.originalFabricCode ?? this.currentRoll()!.expectedFabricCode ?? '')
            : (this.currentRoll()!.expectedFabricCode ?? '')
          : '';
        this.currentKnittingOrder()?.fabricQuantityDetails?.forEach((detail) => {
          if (detail.fabricCode === this.expectedFabricCode) {
            this.plannedLbs += detail.quantity ?? 0;
          }
        });

        this.remainingLbs = this.plannedLbs - (this.currentKnittingOrder()?.issuedQuantity ?? 0);

        this.knittingMachineRollsService.resetServiceState();
        if (this.currentKnittingOrder()) {
          this.knittingMachineRollsService.initializeData(
            assetResult.data ?? [],
            fabricResult.data ?? [],
            rolls.data ?? [],
            this.currentKnittingOrder()!,
          );
        }
        this.isLoading--;
      },
      error: (err) => {
        GlobalMessageUtil.error(
          this.translateService.instant('Stations_Weight_Station_Detail_Error'),
          this.translateService.instant('Stations_Weight_Station_Detail_Complement_Not_Found'),
        );
        this.isLoading = 0;
        throw err;
      },
    });
  }

  // verify if roll already pass for the current station
  verifyRollPast() {
    if (this.isFinalWeightMode) {
      // In final weight mode, check if roll already has a final weight
      const hasFinalWeight = !_.isNil(this.currentRoll()?.finalWeight) && this.currentRoll()!.finalWeight!.weightInLbs > 0;
      this.blockSaveButton.set(hasFinalWeight);
    } else {
      const match = this.currentRoll()?.stationTimeStamps?.some((stationTimeStamp) => {
        return stationTimeStamp.stationAssetId === this.currentReferenceStation();
      });

      if (match) {
        this.blockSaveButton.set(true);
      } else {
        this.blockSaveButton.set(false);
      }
    }
  }

  get enableSaveForRoll() {
    if (this.manualWeightOverride) {
      return !this.blockSaveButton() && this.manualWeightOverride && this.manualWeightValue > 0 && !_.isNil(this.currentRoll());
    } else if (this.isSimpleWeightMode) {
      // Simple weight mode - check if we have a weight value and it's not zero
      return (
        this.simpleWeightData !== null && !this.blockSaveButton() && this.simpleWeightData.weight !== 0 && !_.isNil(this.currentRoll())
      );
    } else {
      // Check if we have a NET weight value and it's not zero
      return (
        this.weightValues.NET !== null && !this.blockSaveButton() && this.weightValues.NET.weight !== 0 && !_.isNil(this.currentRoll())
      );
    }
  }

  get enableSave() {
    if (this.blockSection) {
      return false;
    }

    const currentWeight = this.getCurrentWeightValue();
    if (currentWeight <= 0) {
      return false;
    }

    if (this.isBundleMode) {
      // Bundle mode requires tare selection
      const hasTareSelected = this.selectedTare.value !== null;
      return (
        hasTareSelected && !_.isNil(this.currentBundle()) && this.currentBundle()?.status === ProductionCardBundleStatus.ReadyForWeighing
      );
    } else if (this.isFinalWeightMode) {
      // In final weight mode, enable if roll exists and doesn't already have a final weight
      return !_.isNil(this.currentRoll()) && !this.blockSaveButton();
    } else {
      return this.enableSaveForRoll && !this.showKnittingOrderFinishedAlert;
    }
  }

  returnWeightOfCurrentStation() {
    const filterStations = this.currentRoll()?.stationTimeStamps?.filter((stationTimeStamp) => {
      return stationTimeStamp.stationAssetId === this.currentReferenceStation();
    });

    return filterStations && filterStations.length > 0 ? filterStations[0].weightInLbs : 0;
  }

  // Helper methods for the new weight display
  get netWeight(): number {
    if (this.isSimpleWeightMode) {
      return this.simpleWeightData?.weight ?? 0;
    }
    return this.weightValues.NET?.weight ?? 0;
  }

  get hasWeightData(): boolean {
    if (this.isSimpleWeightMode) {
      return this.simpleWeightData !== null;
    }
    return Object.values(this.weightValues).some((value) => value !== null);
  }

  // Tare helper methods for bundle mode
  getTareLabel(option: TareOption): string {
    const lang = this.translateService.currentLang || 'en';
    return lang === 'es' ? option.labelEs : option.labelEn;
  }

  get selectedTareWeightInLbs(): number {
    const tare = this.selectedTare.value;
    if (!tare) return 0;
    // Convert kg to lbs
    return tare.weightKg * 2.20462;
  }

  get netWeightAfterTare(): number {
    if (!this.isBundleMode) return this.netWeight;
    const grossWeight = this.netWeight;
    const tareWeight = this.selectedTareWeightInLbs;
    return Math.max(0, grossWeight - tareWeight);
  }

  get manualNetWeightAfterTare(): number {
    if (!this.isBundleMode) return this.manualWeightValue;
    // Manual weight value is entered in the current display unit
    // Convert to lbs if showing in Kgs for consistent internal calculation
    const grossWeightInLbs = this.showWeightInKgs() ? this.manualWeightValue * 2.20462 : this.manualWeightValue;
    const tareWeight = this.selectedTareWeightInLbs;
    return Math.max(0, grossWeightInLbs - tareWeight);
  }

  get showKnittingOrderFinishedAlert(): boolean {
    const issuedQuantity = this.currentKnittingOrder()?.issuedQuantity;
    if (!this.currentKnittingOrder()) {
      return false;
    }
    return issuedQuantity !== undefined && issuedQuantity >= this.plannedLbs;
  }

  get bundleHasBeenWeighed(): boolean {
    if (!this.currentBundle()) {
      return false;
    }
    const statuses = [ProductionCardBundleStatus.Weighed, ProductionCardBundleStatus.BRD];
    return statuses.includes(this.currentBundle()!.status);
  }

  get rollHasFinalWeight(): boolean {
    if (!this.currentRoll()) {
      return false;
    }
    return !_.isNil(this.currentRoll()?.finalWeight) && this.currentRoll()!.finalWeight!.weightInLbs > 0;
  }

  getRollFinalWeight(): number {
    return this.currentRoll()?.finalWeight?.weightInLbs ?? 0;
  }

  getLatestStationWeight(): number {
    const timestamps = this.currentRoll()?.stationTimeStamps;
    if (!timestamps || timestamps.length === 0) {
      return 0;
    }
    // Sort by timestamp descending (newest first) and get the first one
    const sorted = [...timestamps].sort((a, b) => new Date(b.timeStamp).getTime() - new Date(a.timeStamp).getTime());
    return sorted[0]?.weightInLbs ?? 0;
  }

  get currentWeightStatus(): string {
    if (this.isSimpleWeightMode) {
      // Simple weight mode doesn't have status - just show unit
      return this.simpleWeightData ? this.simpleWeightData.unit.toUpperCase() : 'No Data';
    }
    if (this.weightValues.NET) return 'NET';
    if (this.weightValues.GROSS) return 'GROSS';
    if (this.weightValues.TARE) return 'TARE';
    if (this.weightValues.UNKNOWN) return 'UNKNOWN';
    return 'No Data';
  }

  getCurrentWeightValue(): number {
    if (this.manualWeightOverride) {
      return this.manualWeightValue;
    }
    if (this.isSimpleWeightMode) {
      return this.simpleWeightData?.weight ?? 0;
    }
    return this.weightValues.NET?.weight ?? 0;
  }

  saveRoll(weight: number) {
    this.isLoading++;

    this.rollProxyService.firstWeight(this.currentReferenceStation(), weight, this.currentRoll()!.id!).subscribe({
      next: (result) => {
        if (result) {
          this.blockSaveButton.set(true);
          this.resetManualWeight();
          // Update the current roll locally with the new weight since API only returns boolean
          const roll = this.currentRoll();
          if (roll) {
            const newTimestamp = {
              stationAssetId: this.currentReferenceStation(),
              timeStamp: new Date(),
              weightInLbs: weight,
            };
            roll.stationTimeStamps = [...(roll.stationTimeStamps || []), newTimestamp];
            this.currentRoll.set({ ...roll });
          }
          this.printQrCode(this.currentRoll()!.number!);
          this.isLoading--;
          GlobalMessageUtil.success(
            this.translateService.instant('Stations_Weight_Station_Detail_Success'),
            this.translateService.instant('Stations_Weight_Station_Detail_Roll_Updated'),
          );
        }
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  saveFinalWeight(weight: number) {
    this.isLoading++;

    this.rollProxyService.setFinalWeight(this.currentRoll()!.number!, weight, this.currentReferenceStation()).subscribe({
      next: (result) => {
        if (result) {
          this.blockSaveButton.set(true);
          this.resetManualWeight();
          this.productionCardControl.disable();
          // Update the current roll with the response data that includes finalWeight
          if (result.data) {
            this.currentRoll.set(result.data);
          }
          this.isLoading--;
          GlobalMessageUtil.success(
            this.translateService.instant('Stations_Weight_Station_Detail_Success'),
            this.translateService.instant('Stations_Weight_Station_Detail_Final_Weight_Saved'),
          );
        }
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  saveBundle(weight: number) {
    this.isLoading++;

    this.productionCardBundleProxyService.weight(this.currentBundle()!.id!, weight).subscribe({
      next: (result) => {
        if (result) {
          this.resetManualWeight();

          const bundle = this.currentBundle();
          this.blockSaveButton.set(true);
          if (bundle) {
            bundle.status = ProductionCardBundleStatus.Weighed;
            bundle.actualWeightInLbs = weight;
            this.currentBundle.set({ ...bundle });
          }
          this.isLoading--;
          GlobalMessageUtil.success(
            this.translateService.instant('Stations_Weight_Station_Detail_Success'),
            this.translateService.instant('Stations_Weight_Station_Detail_Bundle_Updated'),
          );
        }
      },
      error: () => {
        this.isLoading--;
      },
    });
  }

  onSave() {
    if (!this.isFinalWeightMode && this.showKnittingOrderFinishedAlert) {
      GlobalMessageUtil.error(
        this.translateService.instant('Stations_Weight_Station_Detail_Error'),
        this.translateService.instant('Stations_Weight_Station_Detail_Knitting_Order_Exceeded'),
      );
      return;
    }
    const inputWeight = this.getCurrentWeightValue();
    if (!inputWeight) {
      GlobalMessageUtil.error(
        this.translateService.instant('Stations_Weight_Station_Detail_No_Weight_Title'),
        this.translateService.instant('Stations_Weight_Station_Detail_No_Weight_Provided'),
      );
      return;
    }

    // Convert to Lbs if manual override and user entered in Kgs (API only accepts Lbs)
    // Note: For scale values (both simple and non-simple mode), values are already in lbs internally
    let weightInLbs = this.manualWeightOverride && this.showWeightInKgs() ? inputWeight * 2.20462 : inputWeight;

    // For bundle mode, subtract the tare weight
    if (this.isBundleMode && this.selectedTare.value) {
      weightInLbs = Math.max(0, weightInLbs - this.selectedTareWeightInLbs);
    }

    // For display, show in the current unit (convert from internal lbs if showing kg)
    const displayWeight = this.manualWeightOverride ? inputWeight.toFixed(2) : this.showValuesInKgs(weightInLbs).toFixed(2);
    const displayUnit = this.showWeightInKgs() ? 'Kg' : 'Lb';

    const number = this.isBundleMode ? this.currentBundle()!.number : this.currentRoll()!.number;
    let entity = this.translateService.instant('Stations_Weight_Station_Detail_Entity_Roll');
    if (this.isBundleMode) {
      entity = this.translateService.instant('Stations_Weight_Station_Detail_Entity_Bundle');
    } else if (this.isFinalWeightMode) {
      entity = this.translateService.instant('Stations_Weight_Station_Detail_Entity_Roll_Final_Weight');
    }

    this.confirmationService.confirm({
      message: this.translateService.instant('Stations_Weight_Station_Detail_Confirm_Message', {
        weight: displayWeight,
        unit: displayUnit,
        entity: entity,
        number: number,
      }),
      header: this.translateService.instant('Stations_Weight_Station_Detail_Confirm_Header'),
      key: 'wst-dialog',
      acceptLabel: this.translateService.instant('Stations_Weight_Station_Detail_Confirm_Accept'),
      rejectLabel: this.translateService.instant('Stations_Weight_Station_Detail_Confirm_Reject'),
      accept: () => {
        if (this.isBundleMode) {
          this.saveBundle(weightInLbs);
        } else if (this.isFinalWeightMode) {
          this.saveFinalWeight(weightInLbs);
        } else {
          this.saveRoll(weightInLbs);
        }
      },
    });
  }
  onCancel() {
    this.router.navigate(['/stations']);
  }

  printQrCode(rollNumber: string) {
    this.isLoading++;
    this.rollPrintProxyService.getThermalLabelByRollNumber(rollNumber).subscribe({
      next: (response) => {
        this.isLoading--;
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        // Create a hidden iframe
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '-9999px';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.visibility = 'hidden';

        // Add iframe to body
        document.body.appendChild(iframe);

        // Set up the iframe content and print
        iframe.onload = () => {
          try {
            // Small delay to ensure PDF is loaded
            setTimeout(() => {
              if (iframe.contentWindow) {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
              }
            }, 500);
          } catch (e) {
            console.error('Error printing PDF:', e);
            // Fallback: open in new window if hidden printing fails
            window.open(url, '_blank');
            // Clean up
            setTimeout(() => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(url);
            }, 1000);
          }
        };
        this.expectingPrint = true;

        // Set the PDF URL
        iframe.src = url;
        setTimeout(() => {
          if (this.expectingPrint) {
            this.expectingPrint = false;
            this.focusForBarcodeScanner();
          }
        }, 1000);
      },
      error: (err) => {
        this.expectingPrint = false;
        this.isLoading--;
        throw err;
      },
    });
  }

  printFinalLabel(rollNumber: string) {
    this.isLoading++;
    this.rollPrintProxyService.getFinalLabelByRollNumber(rollNumber).subscribe({
      next: (response) => {
        this.isLoading--;
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        // Create a hidden iframe
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.left = '-9999px';
        iframe.style.top = '-9999px';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.visibility = 'hidden';

        // Add iframe to body
        document.body.appendChild(iframe);

        // Set up the iframe content and print
        iframe.onload = () => {
          try {
            // Small delay to ensure PDF is loaded
            setTimeout(() => {
              if (iframe.contentWindow) {
                iframe.contentWindow.focus();
                iframe.contentWindow.print();
              }
            }, 500);
          } catch (e) {
            console.error('Error printing PDF:', e);
            // Fallback: open in new window if hidden printing fails
            window.open(url, '_blank');
            // Clean up
            setTimeout(() => {
              document.body.removeChild(iframe);
              URL.revokeObjectURL(url);
            }, 1000);
          }
        };
        this.expectingPrint = true;

        // Set the PDF URL
        iframe.src = url;
        setTimeout(() => {
          if (this.expectingPrint) {
            this.expectingPrint = false;
            this.focusForBarcodeScanner();
          }
        }, 1000);
      },
      error: (err) => {
        this.expectingPrint = false;
        this.isLoading--;
        throw err;
      },
    });
  }

  parseDate(date: Date, asString = false): Date | string {
    if (asString) {
      return date.toISOString();
    }

    return date;
  }

  loadStationAsset(assetId: string): void {
    this.isLoading++;
    this.assetProxyService.getByAssetId(assetId).subscribe({
      next: (response) => {
        this.isLoading--;
        // The Stations page hides restricted stations; this covers opening one by URL
        if (!WeightStationAccessUtil.canAccess(response.data)) {
          GlobalMessageUtil.warn(
            this.translateService.instant('Stations_Weight_Station_Detail_Warning'),
            this.localeHelperService.translateWithFallback(
              'Stations_Weight_Station_Detail_No_Access',
              'You do not have access to this station',
            ),
          );
          this.router.navigate(['/stations']);
          return;
        }
        this.stationAsset.set(response.data ?? null);
        this.subscribeToScale();
      },
      error: () => {
        this.isLoading--;
        // Without the asset there is no topic to read, but the legacy stations
        // still have their hardcoded one - keep the scale working for them.
        this.subscribeToScale();
      },
    });
  }

  loadUsers(): void {
    this.usersProxyService.getAllUsers().subscribe({
      next: (response) => {
        this.allUsers = response.data || [];
        this.allUsers = this.allUsers.map((user) => {
          const profile = user.profile;
          const nameParts = [profile?.firstName, profile?.middleName, profile?.lastName, profile?.secondLastName].filter(
            (part) => part && part.trim() !== '',
          );

          const fullName = nameParts.length > 0 ? nameParts.join(' ') : user.username;

          return {
            ...user,
            fieldToDisplay: `${fullName} (${user.username})`,
          };
        });
      },
      error: (error) => {
        console.error('Failed to load users:', error.message);
      },
    });
  }

  userTypeahead = (term: string): Observable<UserFull[]> => {
    const searchTerm = term.toLowerCase();
    const filtered = this.allUsers.filter((user) => {
      const profile = user.profile;

      return (
        user.username.toLowerCase().includes(searchTerm) ||
        profile?.firstName?.toLowerCase().includes(searchTerm) ||
        profile?.middleName?.toLowerCase().includes(searchTerm) ||
        profile?.lastName?.toLowerCase().includes(searchTerm) ||
        profile?.secondLastName?.toLowerCase().includes(searchTerm) ||
        user.fieldToDisplay?.toLowerCase().includes(searchTerm)
      );
    });
    return of(filtered);
  };

  public fetchProductionCards = (query: string): Observable<ProductionCard[]> =>
    this.productionCardsProxyService
      .search({ productionCardNumber: query } as ProductionCardSearchCommand, 1, 20)
      .pipe(map(({ data }) => data ?? []));

  onProductionCardSelect(event: AutoCompleteSelectEvent): void {
    this.selectedProductionCard.set((event.value as ProductionCard) ?? null);
    setTimeout(() => this.rollCodeInput?.nativeElement?.focus(), 100);
  }

  onProductionCardClear(): void {
    this.selectedProductionCard.set(null);
  }

  getFabricCodeProgrammedQuantity(fabricCode: string): number {
    let total = 0;
    const useOriginal = this.isGreigeWeightMode;
    this.knittingMachineCombos.forEach((combo) => {
      combo.savedRolls.forEach((roll) => {
        const rollFabricCode = useOriginal ? (roll.originalFabricCode ?? roll.expectedFabricCode) : roll.expectedFabricCode;
        if (rollFabricCode === fabricCode) {
          if (_.isNil(roll.stationTimeStamps) || _.isEmpty(roll.stationTimeStamps)) {
            total += roll.plannedWeight;
          } else {
            total += roll.stationTimeStamps[0].weightInLbs!;
          }
        }
      });
    });
    return total;
  }

  getPlannedQuantityByFabricCode(fabricCode: string): number {
    if (!this.currentKnittingOrder() || !fabricCode) {
      return 0;
    }

    let plannedQuantity = 0;

    this.currentKnittingOrder()?.fabricQuantityDetails?.forEach((detail) => {
      if (detail.fabricCode === fabricCode) {
        plannedQuantity += detail.quantity ?? 0;
      }
    });

    return plannedQuantity;
  }

  get knittingMachineCombos(): KnittingMachineRolls[] {
    const result = this.knittingMachineRollsService.getAllCombos();
    return result;
  }

  private canEdit() {
    const hasEditorRole = AuthService.hasRole(StationsRoles.WeightStationsEditor);
    this.blockAutoCompleteInputs = !hasEditorRole;
    this.blockSection = !hasEditorRole;
  }

  showValuesInKgs(value: number): number {
    // Convert lbs to kg by dividing by 2.20462
    return this.showWeightInKgs() ? value / 2.20462 : value;
  }

  get weightUnit(): string {
    return this.showWeightInKgs() ? 'Kg' : 'Lbs';
  }

  // Convert Kg to Lbs for API (API only accepts Lbs)
  convertToLbsForApi(value: number): number {
    return this.showWeightInKgs() ? value * 2.20462 : value;
  }
}
